import { useState, useCallback } from 'react';
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import {
  IconButton,
  Icon,
  Text,
  SimpleGrid,
  Badge,
  Box,
  Center,
  Image,
  Spinner,
} from '@chakra-ui/react';
import { Tooltip } from '@/components/ui/tooltip';
import { toaster } from '@/components/ui/toaster';
import DestructiveModal from '@/components/alert-dialogs/destructive-modal';
import api from '@/utils/api';

interface Props {
  id: string;
  thumbnails: string[];
  currentThumb: string;
  setCurrentThumb: (thumb: string) => void;
  reload: () => void;
}

export default function ThumbnailBLock({
  id,
  thumbnails,
  currentThumb,
  setCurrentThumb,
  reload,
}: Props) {
  const [settingThumbnail, setSettingThumbnail] = useState<string | null>(null);
  const [deletingFile, setDeletingFile] = useState<string | null>(null);
  const [fileToDelete, setFileToDelete] = useState<string | null>(null);

  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const setThumbnail = useCallback(
    async (fileUrl: string) => {
      if (!fileUrl || !id) return;

      try {
        setSettingThumbnail(fileUrl);
        await api.patch(`/contents/${id}`, { thumbnail: fileUrl });

        setCurrentThumb(fileUrl);
        reload();
        toaster.create({
          title: 'Thumbnail updated successfully',
          type: 'success',
          duration: 2000,
        });
      } catch (err: any) {
        toaster.create({
          type: 'error',
          title: err.response?.data?.error || 'Failed to set thumbnail',
          duration: 3000,
        });
      } finally {
        setSettingThumbnail(null);
      }
    },
    [id, reload, setCurrentThumb],
  );

  const isThumbnail = useCallback(
    (fileUrl: string) => {
      return fileUrl === currentThumb;
    },
    [currentThumb],
  );

  const handleDeleteFile = useCallback(
    async (file: string) => {
      if (!id) return;

      try {
        setDeletingFile(file);
        await api.put(`/media/contents/${id}/thumbnails`, { file_url: file });

        toaster.create({
          title: 'Thumbnail was removed successfully',
          type: 'success',
          duration: 2000,
        });

        if (file === currentThumb) {
          await api.patch(`/contents/${id}`, { thumbnail: '' });
          setCurrentThumb('');
        }
        reload();
      } catch (err: any) {
        toaster.create({
          type: 'error',
          title: err.response?.data?.error || 'Failed to delete thumbnail',
          duration: 3000,
        });
      } finally {
        setDeletingFile(null);
        setFileToDelete(null);
        setShowDeleteModal(false);
      }
    },
    [id, reload, setCurrentThumb, currentThumb],
  );

  const confirmDelete = (file: string) => {
    setFileToDelete(file);
    setShowDeleteModal(true);
  };

  return (
    <>
      {thumbnails.length > 0 && (
        <>
          <SimpleGrid gap={4} columns={{ base: 2, md: 3, lg: 4 }} w={'full'}>
            {thumbnails.map((file, index) => (
              <Box
                key={index}
                borderWidth='1px'
                borderRadius='lg'
                borderColor='border'
                overflow='hidden'
                position='relative'
                aria-label={`Media file ${index + 1}`}
                transition='all 0.2s'
                _hover={{ borderColor: 'border.emphasized', transform: 'translateY(-2px)' }}
              >
                <Center position={'relative'} h='120px'>
                  <Image
                    src={file}
                    alt={'Media file'}
                    boxSize={'120px'}
                    objectFit={'cover'}
                    opacity={deletingFile === file ? 0.5 : 1}
                    loading='lazy'
                  />
                  {!isThumbnail(file) && (
                    <Tooltip content={'Set as thumbnail'}>
                      <IconButton
                        variant='subtle'
                        size={'xs'}
                        aria-label={'Set as thumbnail'}
                        position={'absolute'}
                        top={0.5}
                        left={0.5}
                        onClick={() => setThumbnail(file)}
                        loading={settingThumbnail === file}
                      >
                        <Icon as={CheckCircleIcon} w={4} h={4} />
                      </IconButton>
                    </Tooltip>
                  )}
                  {isThumbnail(file) && (
                    <Box position='absolute' top={0} left={1}>
                      <Badge variant='surface'>current</Badge>
                    </Box>
                  )}

                  <Tooltip content={'Remove a thumbnail'}>
                    <IconButton
                      variant='subtle'
                      size={'xs'}
                      aria-label={'Remove a thumbnail'}
                      position={'absolute'}
                      top={0.5}
                      right={0.5}
                      onClick={() => confirmDelete(file)}
                      loading={deletingFile === file}
                    >
                      <Icon as={XCircleIcon} w={4} h={4} />
                    </IconButton>
                  </Tooltip>

                  {deletingFile === file && (
                    <Box
                      position='absolute'
                      inset={0}
                      bg='blackAlpha.300'
                      display='flex'
                      alignItems='center'
                      justifyContent='center'
                    >
                      <Spinner />
                    </Box>
                  )}
                </Center>
              </Box>
            ))}
          </SimpleGrid>
          <Text as={'i'} display={'flex'} fontSize={'xs'} mt={6}>
            <Icon as={CheckCircleIcon} mt={0.5} mx={1} w={4} h={4} /> Click the check icon to set an
            image as the content thumbnail
          </Text>
        </>
      )}

      {/* Keep mounted; unmounting an open Chakra dialog leaves the body pointer/scroll lock behind. */}
      <DestructiveModal
        isOpen={showDeleteModal}
        onCancel={() => setShowDeleteModal(false)}
        onConfirm={() => fileToDelete && handleDeleteFile(fileToDelete)}
        isLoading={!!deletingFile}
        title={'Confirm Deletion'}
        children={` Are you sure you want to delete this thumbnail? This action cannot be undone.`}
        confirmText={'Confirm'}
      />
    </>
  );
}
